import React from 'react'
import { cn } from '../../utils/cn'

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  helperText?: string
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  fullWidth?: boolean
  containerClassName?: string
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(({
  label,
  error,
  helperText,
  leftIcon,
  rightIcon,
  fullWidth = true,
  className,
  containerClassName,
  disabled,
  required,
  type = 'text',
  ...props
}, ref) => {
  const [isFocused, setIsFocused] = React.useState(false)

  const baseStyles = cn(
    'w-full px-4 py-3',
    'bg-white dark:bg-secondary-900',
    'border rounded-lg',
    'text-secondary-900 dark:text-white',
    'placeholder:text-secondary-400 dark:placeholder:text-secondary-500',
    'focus:outline-none transition-all duration-200',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    'text-base leading-relaxed', // Better readability on mobile
    '-webkit-tap-highlight-color: transparent', // Remove tap highlight on mobile
    type === 'number' && '[-moz-appearance:textfield]', // Remove spinner arrows in Firefox
    type === 'number' && '::-webkit-outer-spin-button, ::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }', // Remove spinner arrows in Chrome/Safari
    'caret-primary-500', // Custom cursor color
    fullWidth && 'w-full',
    className
  )

  const borderStyles = error
    ? 'border-error-500 focus:ring-2 focus:ring-error-500 focus:border-transparent'
    : isFocused
    ? 'border-primary-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent'
    : 'border-secondary-300 dark:border-secondary-600 hover:border-secondary-400 dark:hover:border-secondary-500'

  const labelStyles = cn(
    'block text-sm font-medium mb-2',
    error ? 'text-error-700 dark:text-error-400' : 'text-secondary-700 dark:text-secondary-300'
  )

  return (
    <div className={cn('space-y-1.5', containerClassName)}>
      {label && (
        <label className={labelStyles}>
          {label}
          {required && <span className="text-error-500 ml-1">*</span>}
        </label>
      )}

      <div className="relative">
        {leftIcon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <span className="w-5 h-5 text-secondary-400 dark:text-secondary-500">
              {leftIcon}
            </span>
          </div>
        )}

        {rightIcon && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            <span className="w-5 h-5 text-secondary-400 dark:text-secondary-500">
              {rightIcon}
            </span>
          </div>
        )}

        <input
          ref={ref}
          type={type}
          className={cn(
            baseStyles,
            borderStyles,
            leftIcon && 'pl-10',
            rightIcon && 'pr-10'
          )}
          disabled={disabled}
          onFocus={(e) => {
            setIsFocused(true)
            props.onFocus?.(e)
          }}
          onBlur={(e) => {
            setIsFocused(false)
            props.onBlur?.(e)
          }}
          {...props}
        />

        {error && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <svg className="w-5 h-5 text-error-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        )}
      </div>

      {(error || helperText) && (
        <p className={cn(
          'text-sm',
          error ? 'text-error-600 dark:text-error-400' : 'text-secondary-500 dark:text-secondary-400'
        )}>
          {error || helperText}
        </p>
      )}
    </div>
  )
})

Input.displayName = 'Input'

// TextArea component
export interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  error?: string
  helperText?: string
  fullWidth?: boolean
  resize?: 'none' | 'vertical' | 'horizontal' | 'both'
}

export const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(({
  label,
  error,
  helperText,
  fullWidth = true,
  className,
  disabled,
  required,
  resize = 'vertical',
  rows = 4,
  ...props
}, ref) => {
  const [isFocused, setIsFocused] = React.useState(false)

  const baseStyles = cn(
    'w-full px-4 py-3',
    'bg-white dark:bg-secondary-900',
    'border rounded-lg',
    'text-secondary-900 dark:text-white',
    'placeholder:text-secondary-400 dark:placeholder:text-secondary-500',
    'focus:outline-none transition-all duration-200',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    'leading-relaxed resize-none', // Disable default resize
    fullWidth && 'w-full',
    className
  )

  const resizeStyles = {
    none: 'resize-none',
    vertical: 'resize-y',
    horizontal: 'resize-x',
    both: 'resize',
  }

  const borderStyles = error
    ? 'border-error-500 focus:ring-2 focus:ring-error-500 focus:border-transparent'
    : isFocused
    ? 'border-primary-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent'
    : 'border-secondary-300 dark:border-secondary-600 hover:border-secondary-400 dark:hover:border-secondary-500'

  const labelStyles = cn(
    'block text-sm font-medium mb-2',
    error ? 'text-error-700 dark:text-error-400' : 'text-secondary-700 dark:text-secondary-300'
  )

  return (
    <div className="space-y-1.5">
      {label && (
        <label className={labelStyles}>
          {label}
          {required && <span className="text-error-500 ml-1">*</span>}
        </label>
      )}

      <div className="relative">
        <textarea
          ref={ref}
          className={cn(
            baseStyles,
            borderStyles,
            resizeStyles[resize]
          )}
          rows={rows}
          disabled={disabled}
          onFocus={(e) => {
            setIsFocused(true)
            props.onFocus?.(e)
          }}
          onBlur={(e) => {
            setIsFocused(false)
            props.onBlur?.(e)
          }}
          {...props}
        />

        {error && (
          <div className="absolute right-3 top-3">
            <svg className="w-5 h-5 text-error-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        )}
      </div>

      {(error || helperText) && (
        <p className={cn(
          'text-sm',
          error ? 'text-error-600 dark:text-error-400' : 'text-secondary-500 dark:text-secondary-400'
        )}>
          {error || helperText}
        </p>
      )}
    </div>
  )
})

TextArea.displayName = 'TextArea'

// Select component
export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string
  error?: string
  helperText?: string
  options: Array<{ value: string; label: string }>
  fullWidth?: boolean
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(({
  label,
  error,
  helperText,
  options,
  fullWidth = true,
  className,
  disabled,
  required,
  ...props
}, ref) => {
  const [isFocused, setIsFocused] = React.useState(false)

  const baseStyles = cn(
    'w-full px-4 py-3 pr-10',
    'bg-white dark:bg-secondary-900',
    'border rounded-lg',
    'text-secondary-900 dark:text-white',
    'focus:outline-none transition-all duration-200 appearance-none',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    'cursor-pointer',
    'text-base leading-relaxed',
    fullWidth && 'w-full',
    className
  )

  const borderStyles = error
    ? 'border-error-500 focus:ring-2 focus:ring-error-500 focus:border-transparent'
    : isFocused
    ? 'border-primary-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent'
    : 'border-secondary-300 dark:border-secondary-600 hover:border-secondary-400 dark:hover:border-secondary-500'

  const labelStyles = cn(
    'block text-sm font-medium mb-2',
    error ? 'text-error-700 dark:text-error-400' : 'text-secondary-700 dark:text-secondary-300'
  )

  return (
    <div className="space-y-1.5">
      {label && (
        <label className={labelStyles}>
          {label}
          {required && <span className="text-error-500 ml-1">*</span>}
        </label>
      )}

      <div className="relative">
        <select
          ref={ref}
          className={cn(baseStyles, borderStyles)}
          disabled={disabled}
          onFocus={(e) => {
            setIsFocused(true)
            props.onFocus?.(e)
          }}
          onBlur={(e) => {
            setIsFocused(false)
            props.onBlur?.(e)
          }}
          {...props}
        >
          <option value="">Chọn...</option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        {/* Custom dropdown arrow */}
        <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none">
          <svg className="w-5 h-5 text-secondary-400 dark:text-secondary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {(error || helperText) && (
        <p className={cn(
          'text-sm',
          error ? 'text-error-600 dark:text-error-400' : 'text-secondary-500 dark:text-secondary-400'
        )}>
          {error || helperText}
        </p>
      )}
    </div>
  )
})

Select.displayName = 'Select'

export interface SwitchProps {
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
  label?: string
}

export const Switch: React.FC<SwitchProps> = ({ checked, onChange, disabled, label }) => {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors',
        'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
        disabled && 'cursor-not-allowed opacity-50',
        checked ? 'bg-primary-600' : 'bg-secondary-300 dark:bg-secondary-700',
      )}
    >
      <span
        className={cn(
          'inline-block h-5 w-5 rounded-full bg-white shadow transition-transform',
          checked ? 'translate-x-5' : 'translate-x-1',
        )}
      />
    </button>
  )
}

export default Input
